import type { FiatPrice, TotalTripPricing } from "@b2bportal/air-booking-api";
import type { Prices } from "@b2bportal/core-types";

/**
 * Fixed price flights do not return a trip total in the quote breakdown, so we calculate it manually.
 *
 * Only the `fiat` total is returned; `accountSpecific` is always empty. The backend
 * prices each product at its own rate (CFAR/DAFAR at the fintech rate, grid
 * taxes/overage at the grid rate), so the trip total in points comes solely from the
 * `pointsToCash/quote` `maximum` (see `getTripTotalInRewards`), never from a frontend
 * estimate.
 *
 * @param totalPricing - The total pricing from the quote breakdown
 * @param selectedSeatsFiatPrice - The fiat price of the selected seats
 * @param tripInsuranceFiatPrice - The fiat price of the trip insurance
 * @param additionalPointsPurchaseFiatPrice - The fiat price of additional points purchased
 * @param cfarFiatPrice - The fiat price of the CFAR (Cancel For Any Reason) premium
 * @param disruptionFiatPrice - The fiat price of the disruption protection premium
 * @returns The total pricing with the products included
 */
export const getFixedPriceTotalWithProducts = (
  totalPricing: TotalTripPricing,
  selectedSeatsFiatPrice: FiatPrice | undefined,
  tripInsuranceFiatPrice: FiatPrice | undefined,
  additionalPointsPurchaseFiatPrice: FiatPrice | undefined,
  cfarFiatPrice?: FiatPrice,
  disruptionFiatPrice?: FiatPrice
): Prices => {
  const { fiat } = totalPricing.taxes;

  // Total fiat value - round to 2 decimals to avoid floating point errors
  // This ensures the math adds up when rewards are subtracted later
  const updatedFiatValue =
    Math.round(
      (fiat.value +
        (selectedSeatsFiatPrice?.value ?? 0) +
        (totalPricing.fixedPointsOverage?.fiat.value ?? 0) +
        (additionalPointsPurchaseFiatPrice?.value ?? 0) +
        (tripInsuranceFiatPrice?.value ?? 0) +
        (cfarFiatPrice?.value ?? 0) +
        (disruptionFiatPrice?.value ?? 0)) *
        100
    ) / 100;

  return {
    fiat: {
      ...fiat,
      value: updatedFiatValue,
    },
    accountSpecific: {},
  };
};
